import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { OrganizationAutopilotProfileStatus } from "schemas/dashboard";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { PAGE_VISITED } from "constants/trackingProps";
import AutopilotOnboardingProgress from "features/Auth/Onboarding/AutopilotOnboarding/Progress";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import AutomationCard from "../AutomationCard";
import AutopilotSettings from "../AutopilotSettings";
import AutomationBrandsDetail from "../BrandsToReview/AutomationBrandsDetail";
import BrandsToReviewPreview from "../BrandsToReview/BrandsToReviewPreview";
import QueuedBrandsReview from "../BrandsToReview/QueuedBrandsReview";
import EditAutomationDrawer from "../EditAutomationDrawer";
import NewAutomation from "../NewAutomation";
import ActivationDialog from "../NewAutomation/ActivationDialog";
import {
  AutomationEmailMetrics,
  OutreachAutomation,
  OutreachAutomationType,
} from "../types";

const PER_PAGE = 20;

export default function SubscribedView() {
  const { getToken } = useAuth();
  const { currentOrg, organizationAutopilotProfile } = useContext(
    OrganizationUserContext,
  );
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const {
    emailHealth,
    setOpenIntegrationDialog,
    setIntegrationDialogSubtitle,
  } = useContext(UserIntegrationsContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sidebarOpen = discoverTab !== null;

  const handleNewAutomationClick = () => {
    if (!emailHealth) {
      setIntegrationDialogSubtitle("Required to start a new automation");
      setOpenIntegrationDialog(true);
      return;
    }
    navigate("new/source");
  };

  const [automations, setAutomations] = useState<OutreachAutomation[]>([]);
  const [statsById, setStatsById] = useState<
    Record<number, AutomationEmailMetrics>
  >({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pauseAll, setPauseAll] = useState(false);
  const [notifyOnNewBrands, setNotifyOnNewBrands] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Dialogs
  const [pauseTarget, setPauseTarget] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [runTarget, setRunTarget] = useState<number | null>(null);
  const [sendNotification, setSendNotification] = useState(false);

  // Edit drawer
  const [editAutomation, setEditAutomation] =
    useState<OutreachAutomation | null>(null);
  const [brandsRefreshKey, setBrandsRefreshKey] = useState(0);

  const [activatedSourceType, setActivatedSourceType] =
    useState<OutreachAutomationType | null>(null);
  const [pendingAutomationId, setPendingAutomationId] = useState<number | null>(
    null,
  );

  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  const fetchAutomations = async (cursor?: [boolean, boolean, number]) => {
    if (!currentOrg?.id) return;
    if (cursor) {
      setLoading(true);
    } else {
      setInitialLoading(true);
    }

    let url = `/api/organization/${currentOrg.id}/outreach-automations?per_page=${PER_PAGE}`;
    if (cursor) {
      url += `&cursor=${JSON.stringify(cursor)}`;
    }

    try {
      const res = await fetcherAuth(getToken, url, "GET", {});
      if (cursor) {
        setAutomations((prev) => [...prev, ...(res.outreachAutomations ?? [])]);
      } else {
        setAutomations(res.outreachAutomations ?? []);
      }
      setTotal(res.total);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const refreshAutomations = async () => {
    if (!currentOrg?.id) return;
    setRefreshing(true);
    try {
      const url = `/api/organization/${currentOrg.id}/outreach-automations?per_page=${PER_PAGE}`;
      const res = await fetcherAuth(getToken, url, "GET", {});
      setAutomations(res.outreachAutomations ?? []);
      setTotal(res.total);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchSettings = async () => {
    if (!currentOrg?.id) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automation-settings`,
      );
      setPauseAll(res.outreachAutomationSetting?.pauseAll ?? false);
      setNotifyOnNewBrands(
        res.outreachAutomationSetting?.notifyOnNewBrands ?? true,
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchStats = async (ids: number[]) => {
    if (!currentOrg?.id || ids.length === 0) return;
    const params = ids.map((id) => `automation_ids=${id}`).join("&");
    const url = `/api/organization/${currentOrg.id}/outreach-automations/stats?${params}`;
    try {
      const res = await fetcherAuth(getToken, url, "GET", {});
      const next: Record<number, AutomationEmailMetrics> = {};
      for (const stat of (res.automationStats ??
        []) as AutomationEmailMetrics[]) {
        next[stat.automationId] = stat;
      }
      setStatsById((prev) => ({ ...prev, ...next }));
    } catch {
      // Stats are non-critical; leave the card without metrics on failure.
    }
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    fetchAutomations();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    const missing = automations
      .filter(
        (a) =>
          a.status !== "draft" &&
          a.status !== "pending" &&
          !(a.id in statsById),
      )
      .map((a) => a.id);
    if (missing.length > 0) fetchStats(missing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automations]);

  useEffect(() => {
    const raw = searchParams.get("openAutomationId");
    if (!raw) return;
    const id = Number(raw);
    if (!Number.isFinite(id)) return;
    const match = automations.find((a) => a.id === id);
    if (!match) return;
    setEditAutomation(match);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openAutomationId");
        return next;
      },
      { replace: true },
    );
  }, [automations, searchParams, setSearchParams]);

  const handleActivated = (automation: OutreachAutomation) => {
    navigate("/dashboard/autopilot");
    setActivatedSourceType(automation.type);
    if (automation.type === "bento_database") {
      setPendingAutomationId(automation.id);
    }
  };

  const handleDraftCreated = (automation: OutreachAutomation) => {
    setAutomations((prev) => [automation, ...prev]);
    setTotal((prev) => (prev != null ? prev + 1 : prev));
  };

  const handleAutomationUpdated = (automation: OutreachAutomation) => {
    setAutomations((prev) => {
      const updated = prev.map((a) =>
        a.id === automation.id ? automation : a,
      );
      // Re-sort: drafts (0) → active (1) → paused (2), then by createdAt desc
      updated.sort((a, b) => {
        const aBucket =
          a.status === "draft"
            ? 0
            : a.status === "paused" || a.status === "exhausted"
              ? 2
              : 1;
        const bBucket =
          b.status === "draft"
            ? 0
            : b.status === "paused" || b.status === "exhausted"
              ? 2
              : 1;
        if (aBucket !== bBucket) return aBucket - bBucket;
        return b.createdAt - a.createdAt;
      });
      return updated;
    });
  };

  useEffect(() => {
    if (
      isPaginationRefVisible &&
      automations.length > 0 &&
      total &&
      automations.length < total &&
      !loading
    ) {
      const last = automations[automations.length - 1];
      fetchAutomations([
        last.status === "draft",
        last.status === "paused" || last.status === "exhausted",
        last.createdAt,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible, automations.length]);

  const handlePauseConfirm = async () => {
    if (!currentOrg?.id || !pauseTarget) return;
    const id = pauseTarget;
    setPauseTarget(null);
    setUpdatingId(id);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${id}/pause`,
        "POST",
      );
      await refreshAutomations();
      setBrandsRefreshKey((k) => k + 1);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleResume = async (automationId: number) => {
    if (!currentOrg?.id) return;
    setUpdatingId(automationId);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${automationId}/resume`,
        "POST",
      );
      await refreshAutomations();
      setBrandsRefreshKey((k) => k + 1);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteDraft = async (id: number) => {
    if (!currentOrg?.id) return;
    setAutomations((prev) => prev.filter((a) => a.id !== id));
    setTotal((prev) => (prev ? prev - 1 : prev));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${id}`,
        "DELETE",
      );
    } catch (error) {
      setErrorAlert(error);
      refreshAutomations();
    }
  };

  const handleDeleteConfirmDirect = async (id: number) => {
    if (!currentOrg?.id) return;
    setUpdatingId(id);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${id}`,
        "DELETE",
      );
      setAutomations((prev) => prev.filter((a) => a.id !== id));
      setTotal((prev) => (prev ? prev - 1 : prev));
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteTarget(null);
    await handleDeleteConfirmDirect(deleteTarget);
  };

  const handleRun = (id: number) => {
    setRunTarget(id);
  };

  const handleRunConfirm = async () => {
    if (!runTarget) return;
    const id = runTarget;
    setRunTarget(null);
    setUpdatingId(id);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-automations/${id}/run`,
        "POST",
        {},
        { sendNotification },
      );
      await refreshAutomations();
      setBrandsRefreshKey((k) => k + 1);
      setAlert(
        `Run complete: found ${res.bentoBrands?.length ?? 0} (needed ${res.needed ?? "?"}, existing ${res.existing ?? 0})`,
        "success",
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleEditSaved = (
    updated: OutreachAutomation,
    brandsChanged?: boolean,
  ) => {
    const prev = automations.find((a) => a.id === updated.id);
    const statusChanged = prev && prev.status !== updated.status;
    setAutomations((list) =>
      list.map((a) => (a.id === updated.id ? updated : a)),
    );
    if (brandsChanged || statusChanged) {
      setBrandsRefreshKey((k) => k + 1);
    }
    if (statusChanged) {
      refreshAutomations();
    }
  };

  const automationsList = (
    <>
      <Box sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
        {/* Onboarding Progress Banner */}
        {organizationAutopilotProfile &&
          organizationAutopilotProfile.status !==
            OrganizationAutopilotProfileStatus.completed && (
            <Box sx={{ mb: 3 }}>
              <AutopilotOnboardingProgress />
            </Box>
          )}

        {/* Top Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, #014339 0%, rgba(1,67,57,0.85) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Box
                component="span"
                className="fa-solid fa-bolt"
                sx={{ fontSize: 16, color: "yellow.main" }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  lineHeight: "28px",
                  color: "primary.main",
                }}
              >
                Brand Automations
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "20px",
                  color: "rgba(1,67,57,0.6)",
                }}
              >
                Automatically discover and pitch brands from Instagram, Google
                and more
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => navigate("settings")}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                border: "1px solid rgba(1,67,57,0.15)",
                color: "rgba(1,67,57,0.6)",
                "&:hover": {
                  backgroundColor: "rgba(1,67,57,0.05)",
                  color: "primary.main",
                },
              }}
            >
              <Box
                component="span"
                className="fa-solid fa-gear"
                sx={{ fontSize: 16 }}
              />
            </IconButton>
            <Button
              variant="contained"
              disableElevation
              onClick={handleNewAutomationClick}
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: sidebarOpen ? 0 : { xs: 0, sm: 2 },
                py: sidebarOpen ? 0 : { xs: 0, sm: 1 },
                minWidth: sidebarOpen ? 40 : { xs: 40, sm: undefined },
                height: sidebarOpen ? 40 : { xs: 40, sm: "auto" },
                "&:hover": {
                  backgroundColor: "#012e27",
                },
              }}
            >
              <Box
                component="span"
                className="fa-solid fa-plus"
                sx={{ fontSize: 12, mr: sidebarOpen ? 0 : { xs: 0, sm: 1 } }}
              />
              <Box
                component="span"
                sx={{
                  display: sidebarOpen ? "none" : { xs: "none", sm: "inline" },
                }}
              >
                New Automation
              </Box>
            </Button>
          </Box>
        </Box>

        {/* Pause All Banner */}
        {pauseAll && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "12px",
              border: "1px solid #ffd6a8",
              backgroundColor: "#fff7ed",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-circle-pause"
              sx={{ fontSize: 18, color: "#ca3500", flexShrink: 0 }}
            />
            <Typography sx={{ fontSize: 14, color: "#ca3500" }}>
              All automations are paused.{" "}
              <Box
                component="span"
                onClick={() => navigate("settings")}
                sx={{
                  fontWeight: 600,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Go to settings
              </Box>{" "}
              to resume.
            </Typography>
          </Box>
        )}

        {/* Brands to Review Preview */}
        <BrandsToReviewPreview
          refreshKey={brandsRefreshKey}
          hasActiveAutomations={automations.some(
            (a) => a.status === "live" || a.status === "pending",
          )}
          pendingAutomationId={pendingAutomationId}
          onPendingAutomationResolved={() => {
            setPendingAutomationId(null);
            refreshAutomations();
          }}
        />

        {/* Automations List */}
        <Box sx={{ position: "relative" }}>
          {refreshing && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255,255,255,0.6)",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "12px",
              }}
            >
              <CircularProgress size={28} sx={{ color: "primary.main" }} />
            </Box>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: "24px",
              color: "primary.main",
              mb: 2,
            }}
          >
            Your Automations
          </Typography>

          {initialLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Skeleton variant="rounded" width="100%" height={100} />
              <Skeleton variant="rounded" width="100%" height={100} />
              <Skeleton variant="rounded" width="100%" height={100} />
            </Box>
          ) : automations.length === 0 ? (
            <Box
              sx={{
                border: "2px dashed rgba(1,67,57,0.15)",
                borderRadius: "14px",
                py: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "rgba(1,67,57,0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-bolt"
                  sx={{ fontSize: 24, color: "rgba(1,67,57,0.3)" }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  color: "rgba(1,67,57,0.6)",
                }}
              >
                No automations yet
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "rgba(1,67,57,0.4)",
                  textAlign: "center",
                  maxWidth: 320,
                }}
              >
                Create your first automation to start discovering and pitching
                brands automatically.
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={handleNewAutomationClick}
                sx={{
                  mt: 1,
                  backgroundColor: "primary.main",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "#012e27",
                  },
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-plus"
                  sx={{ fontSize: 12, mr: 1 }}
                />
                New Automation
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {automations.map((automation, index) => (
                <Box
                  ref={automations.length - 5 === index ? paginationRef : null}
                  key={automation.id}
                >
                  <AutomationCard
                    automation={automation}
                    stats={statsById[automation.id]}
                    onPause={(id) => setPauseTarget(id)}
                    onResume={handleResume}
                    onDelete={(id) => {
                      const a = automations.find((a) => a.id === id);
                      if (a?.status === "draft") {
                        handleDeleteDraft(id);
                      } else {
                        setDeleteTarget(id);
                      }
                    }}
                    onEdit={setEditAutomation}
                    onRun={handleRun}
                    isUpdating={updatingId === automation.id}
                  />
                </Box>
              ))}
              {loading && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Skeleton variant="rounded" width="100%" height={100} />
                  <Skeleton variant="rounded" width="100%" height={100} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Edit drawer */}
      <EditAutomationDrawer
        automation={editAutomation}
        onClose={() => setEditAutomation(null)}
        onSaved={handleEditSaved}
        onDelete={(id) => {
          setEditAutomation(null);
          handleDeleteConfirmDirect(id);
        }}
      />

      {/* Pause confirmation dialog */}
      <ConfirmDialog
        header="Pause Automation?"
        subheader={
          <Typography>
            Pausing will stop this automation from discovering and pitching new
            brands. Any emails already scheduled will still be delivered as
            planned. You can resume at any time.
          </Typography>
        }
        open={!!pauseTarget}
        handleConfirm={handlePauseConfirm}
        handleCancel={() => setPauseTarget(null)}
        handleClose={() => setPauseTarget(null)}
        confirmText="Pause"
        cancelText="Cancel"
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        header="Delete Automation?"
        subheader={
          <Typography>
            This will permanently stop this automation. Any emails already
            scheduled will still be delivered as planned.
          </Typography>
        }
        open={!!deleteTarget}
        handleConfirm={handleDeleteConfirm}
        handleCancel={() => setDeleteTarget(null)}
        handleClose={() => setDeleteTarget(null)}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Admin run confirmation dialog */}
      <ConfirmDialog
        header="Run Automation?"
        subheader={
          <Box>
            <Typography sx={{ mb: 1 }}>
              This will immediately run brand discovery for this automation.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                />
              }
              label="Send email notification to user if new brands are found"
            />
          </Box>
        }
        open={!!runTarget}
        handleConfirm={handleRunConfirm}
        handleCancel={() => setRunTarget(null)}
        handleClose={() => setRunTarget(null)}
        confirmText="Run"
        cancelText="Cancel"
      />

      <ActivationDialog
        open={activatedSourceType !== null}
        sourceType={activatedSourceType}
        onClose={() => setActivatedSourceType(null)}
      />
    </>
  );

  return (
    <Routes>
      <Route path="/" element={automationsList} />
      <Route
        path="brands-to-review"
        element={<QueuedBrandsReview automations={automations} />}
      />
      <Route
        path="brands-to-review/:automationId"
        element={<AutomationBrandsDetail automations={automations} />}
      />
      <Route
        path="new/source"
        element={
          <NewAutomation
            onDraftCreated={handleDraftCreated}
            onAutomationUpdated={handleAutomationUpdated}
            onActivated={handleActivated}
          />
        }
      />
      <Route
        path="new/:draftId/*"
        element={
          <NewAutomation
            onDraftCreated={handleDraftCreated}
            onAutomationUpdated={handleAutomationUpdated}
            onActivated={handleActivated}
          />
        }
      />
      <Route
        path="settings"
        element={
          <AutopilotSettings
            pauseAll={pauseAll}
            setPauseAll={setPauseAll}
            notifyOnNewBrands={notifyOnNewBrands}
            setNotifyOnNewBrands={setNotifyOnNewBrands}
            loading={settingsLoading}
          />
        }
      />
    </Routes>
  );
}
